import { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { useThrottle } from '~/lib/hooks/useThrottle';
import { classNames } from '~/utils/classNames';

interface StepSliderMark {
  value: number;
  label: string;
  icon?: string;
  description?: string;
}

interface StepSliderProps {
  value: number;
  onChange: (value: number) => void;
  minValue?: number;
  maxValue?: number;
  className?: string;
  marks?: StepSliderMark[];
  disabled?: boolean;
}

export const StepSlider = (props: StepSliderProps) => {
  const { value, onChange, minValue = 0, maxValue = 100, className, marks = [], disabled = false } = props;

  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const innerValueRef = useRef<null | number>(null);
  const markHeightRef = useRef(0);
  const [innerValue, setInnerValue] = useState<number | null>(null);
  const [markHeight, setMarkHeight] = useState(0);

  const min = minValue;
  const max = maxValue;
  const stepsCount = Math.floor(max - min);

  const getStepIndex = (val: number) => Math.round(val - min);
  const getStepValue = (idx: number) => min + idx;
  const currentIdx = getStepIndex(innerValue === null ? value : innerValue);

  const getClosestStepIdx = useCallback(
    (clientX: number) => {
      if (!trackRef.current) {
        return currentIdx;
      }

      const rect = trackRef.current.getBoundingClientRect();
      const percent = (clientX - rect.left) / rect.width;
      const idx = Math.round(percent * stepsCount);

      return Math.max(0, Math.min(idx, stepsCount));
    },
    [currentIdx, stepsCount],
  );

  const handleTrackClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      const idx = getClosestStepIdx(e.clientX);
      onChange(getStepValue(idx));
    },
    [disabled, getClosestStepIdx, getStepValue, onChange],
  );

  const onDragProgress = useCallback(
    (percent: number) => {
      const floatIdx = percent * stepsCount;
      const nextIdx = Math.max(0, Math.min(max, Math.round(floatIdx)));
      const cind = innerValueRef.current === null ? currentIdx : innerValueRef.current;

      if (nextIdx !== cind && Math.abs(floatIdx - cind) >= 0.5) {
        setInnerValue(getStepValue(nextIdx));
        innerValueRef.current = getStepValue(nextIdx);
      }
    },
    [stepsCount, max, currentIdx, getStepValue],
  );

  const onDragEnd = useCallback(() => {
    draggingRef.current = false;

    if (innerValue !== value) {
      onChange(innerValueRef.current!);
      innerValueRef.current = null;
      setInnerValue(null);
    }
  }, [innerValue, value, onChange]);

  const handleThumbMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingRef.current || !trackRef.current) {
        return;
      }

      const rect = trackRef.current.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      onDragProgress(percent);
    },
    [onDragProgress],
  );

  const handleThumbMouseUp = useCallback(() => {
    onDragEnd();
    window.removeEventListener('mousemove', handleThumbMouseMove);
    window.removeEventListener('mouseup', handleThumbMouseUp);
  }, [onDragEnd, handleThumbMouseMove]);

  const handleThumbMouseDown = useCallback(() => {
    if (disabled) {
      return;
    }

    draggingRef.current = true;
    window.addEventListener('mousemove', handleThumbMouseMove);
    window.addEventListener('mouseup', handleThumbMouseUp);
  }, [disabled, handleThumbMouseMove, handleThumbMouseUp]);

  const handleThumbTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!draggingRef.current || !trackRef.current || !e.touches[0]) {
        return;
      }

      const rect = trackRef.current.getBoundingClientRect();
      const percent = (e.touches[0].clientX - rect.left) / rect.width;
      onDragProgress(percent);
    },
    [onDragProgress],
  );

  const handleThumbTouchEnd = useCallback(() => {
    onDragEnd();
    window.removeEventListener('touchmove', handleThumbTouchMove);
    window.removeEventListener('touchend', handleThumbTouchEnd);
  }, [onDragEnd, handleThumbTouchMove]);

  const handleThumbTouchStart = useCallback(() => {
    if (disabled) {
      return;
    }

    draggingRef.current = true;
    window.addEventListener('touchmove', handleThumbTouchMove);
    window.addEventListener('touchend', handleThumbTouchEnd);
  }, [disabled, handleThumbTouchMove, handleThumbTouchEnd]);

  const percent = (currentIdx / stepsCount) * 100;

  const throttledOnDragProgress = useThrottle(setMarkHeight, 50);

  const recordHeight = useCallback((height: number) => {
    markHeightRef.current = Math.max(markHeightRef.current, height);
    throttledOnDragProgress(markHeightRef.current);
  }, []);

  useEffect(() => {
    if (!marks.length || !trackRef.current) {
      return;
    }

    const width = Math.floor(100 / marks.length - 2); // 2% for the left and right padding
    trackRef.current.style.setProperty('--step-slider-max-mark-width', `${width}%`);
  }, [marks.length]);

  return (
    <div className={classNames('w-full flex flex-col items-center flex w-full items-center gap-5', className)}>
      <div
        ref={trackRef}
        className={classNames(
          'relative w-full h-2 bg-accent-600/12 rounded cursor-pointer select-none flex',

          disabled ? 'opacity-50 pointer-events-none' : '',
        )}
        onClick={handleTrackClick}
      >
        <span
          className="absolute h-2 bg-bolt-ds-brand rounded"
          style={{ width: `${percent}%`, left: 0, top: 0, zIndex: 1 }}
        />

        {marks.map((mark, index) => {
          const lastIndex = marks.length - 1;
          const markIdx = getStepIndex(mark.value);
          const perc = (markIdx / stepsCount) * 100;

          let shift = 0;

          if (index === 0) {
            shift = 6;
          } else if (index === lastIndex) {
            shift = -6;
          }

          const left = `calc(${perc}% + ${shift}px)`;

          return (
            <Fragment key={mark.value}>
              <span className="absolute bg-bolt-ds-brand rounded-lg w-1 h-3 top-1/2 -translate-1/2" style={{ left }} />
              <Mark
                className={classNames('text-center max-w-[var(--step-slider-max-mark-width)]', {
                  'items-start text-start': index === 0,
                  'items-end text-end': index === lastIndex,
                  '-translate-x-1/2': index !== 0 && index !== lastIndex,
                })}
                mark={mark}
                onChange={onChange}
                style={index === lastIndex ? { right: 0 } : { left: `${perc}%` }}
                onHeight={recordHeight}
              />
            </Fragment>
          );
        })}

        <button
          className={classNames(
            'absolute top-1/2 -translate-y-1/2 z-layer-2 cursor-grab size-5 bg-bolt-ds-surface rounded-full border-2 border-bolt-ds-brand',
            {
              'translate-x-[-10px]': percent > 0 && percent < 100,
              'translate-x-[-20px]': percent === 100,
            },
          )}
          style={{ left: `${percent}%` }}
          tabIndex={0}
          role="slider"
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          onMouseDown={handleThumbMouseDown}
          onTouchStart={handleThumbTouchStart}
        />
      </div>
      <div style={{ height: `${markHeight}px` }} />
    </div>
  );
};

interface MarkProps {
  mark: StepSliderMark;
  onChange: (value: number) => void;
  onHeight: (value: number) => void;
  className?: string;
  style?: CSSProperties;
}

const Mark = ({ mark, onChange, className, style, onHeight }: MarkProps) => {
  const handleChange = useCallback(() => {
    onChange(mark.value);
  }, [mark.value, onChange]);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const { height } = ref.current.getBoundingClientRect();

    onHeight(height);
  }, [onHeight, mark]);

  return (
    <div
      className={classNames('absolute top-6.5 flex flex-col cursor-pointer gap-1 items-center', className)}
      onClick={handleChange}
      style={style}
      ref={ref}
    >
      <p className="flex gap-1 text-sm font-medium">
        {mark.icon && <span className={`block size-4 self-center ${mark.icon}`}></span>}
        {mark.label}
      </p>
      {mark.description && <p className="text-sm text-bolt-elements-textSecondary">{mark.description}</p>}
    </div>
  );
};
